/**
 * Pure parsers for tool-call inputs. Used by the renderer's ToolCallDetails to
 * extract human-readable summaries (file path, shell command, edit strings) and
 * to shorten paths for the summary row.
 *
 * Kept dependency-free (no React, no DOM, no Node-only APIs) so the renderer
 * imports them via `@shared/toolCallParsers` and Vitest can exercise them
 * directly.
 */

/** Extract `file_path` (or `path`) from a JSON tool input. */
export function extractFilePath(input: string): string | null {
  try {
    const obj = JSON.parse(input) as Record<string, unknown>;
    const p = obj.file_path ?? obj.path;
    return typeof p === 'string' ? p : null;
  } catch {
    return null;
  }
}

/** Extract `old_string`/`new_string` from an edit_file tool input. */
export function extractEditStrings(input: string): { oldStr: string; newStr: string } | null {
  try {
    const obj = JSON.parse(input) as Record<string, unknown>;
    if (typeof obj.old_string === 'string' && typeof obj.new_string === 'string') {
      return { oldStr: obj.old_string, newStr: obj.new_string };
    }
  } catch {
    /* fallthrough */
  }
  return null;
}

/** Extract a single-line shell command preview from a Bash/bash tool input. */
export function extractBashCommand(input: string): string | null {
  try {
    const obj = JSON.parse(input) as Record<string, unknown>;
    const cmd = obj.command ?? obj.cmd;
    if (typeof cmd !== 'string') return null;
    const firstLine = cmd.split('\n')[0]?.trim() ?? '';
    return firstLine.length > 0 ? firstLine : null;
  } catch {
    return null;
  }
}

/** Last two segments of a path — "src/foo.ts" instead of just "foo.ts". */
export function shortPath(p: string): string {
  const parts = p.split('/').filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? p;
  return parts.slice(-2).join('/');
}

/**
 * Human display name for a tool. Strips the MCP namespace prefix that the
 * Claude Code / subscription path attaches to OneStone tools (`mcp__atlas__foo`)
 * so the chat panel shows `foo`, not the wire name. The SDK path already sends
 * clean names, so this is a no-op there.
 */
export function displayToolName(name: string): string {
  return name.replace(/^mcp__[a-z0-9-]+__/i, '');
}

/** Salient string args, in priority order, for the collapsed summary chip. */
const SUMMARY_KEYS = [
  'query',
  'q',
  'name',
  'skill',
  'id',
  'url',
  'project',
  'projectId',
  'title',
  'message',
  'prompt',
  'content',
  'text',
];

function clipPreview(s: string, max = 80): string {
  const oneLine = s.replace(/\s+/g, ' ').trim();
  return oneLine.length > max ? `${oneLine.slice(0, max - 1)}…` : oneLine;
}

/**
 * One-line preview of a tool input for the collapsed summary chip — used for
 * tools without a dedicated preview (file/shell/edit have their own). Picks the
 * most salient string argument; never echoes the tool name. Returns null when
 * there's nothing useful to show (so the chip is omitted rather than duplicated).
 */
export function summarizeToolInput(input: string): string | null {
  let obj: Record<string, unknown>;
  try {
    obj = JSON.parse(input) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object') return null;

  for (const key of SUMMARY_KEYS) {
    const v = obj[key];
    if (typeof v === 'string' && v.trim().length > 0) return clipPreview(v);
  }
  // Fallback: first non-empty string arg, then first scalar.
  for (const v of Object.values(obj)) {
    if (typeof v === 'string' && v.trim().length > 0) return clipPreview(v);
  }
  for (const v of Object.values(obj)) {
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  }
  return null;
}

/**
 * Detect tool-call-shaped plain text in assistant output — the recurring
 * "tools as plain text" failure where the model narrates a tool invocation
 * (bare argument JSON, `<function_calls>`/`<invoke>` XML, `<tool_call>` tags)
 * instead of calling the tool. Happens when the model has no real tool access
 * (MCP handshake failure) and fabricates the whole exchange. Shared here
 * (dependency-free) so BOTH the chat path (log + tripwire) and the routine
 * executor (hard gate: never route fabricated output to knowledge)
 * check the same predicate.
 */
export function looksLikeFakeToolCall(text: string): boolean {
  if (text.includes('<function_calls>') || text.includes('<invoke ')) return true;
  // 2026-07-03 routine fabrication used <tool_call> JSON blocks.
  if (text.includes('<tool_call>') || text.includes('<tool_result>')) return true;
  // A line-anchored JSON object opening with a canonical tool-argument key.
  return /^\s*\{\s*"(command|file_path|path|query|pattern|url)"\s*:/m.test(text);
}
